import React from "react";
import { ThemeProvider, createMuiTheme } from "@material-ui/core/styles";
import { useSelector } from "react-redux";
import i18n from "./i18n";
import Router from "./Router";

import {
  green,
  pink,
  amber,
  cyan,
  red,
  indigo,
} from "@material-ui/core/colors";
import { Typography } from "@material-ui/core";

import { Helmet } from "react-helmet";
import { LANG_TURKISH } from "./constants/language";

function App() {
  const paletteType = useSelector((state) => state.theme);
  const userPrefersDarkMode = paletteType === "dark";

  const theme = React.useMemo(
    () =>
      createMuiTheme({
        overrides: {
          "@global": {
            "*": {},
            "&:webkit-scrollbar": {
              width: 10,
              backgroundColor: "#f5f5f5",
            },
            "&:webkit-scrollbar-thumb": {
              borderRadius: 5,
              backgroundColor: "#555555",
            },
          },

          MuiButton: {
            outlinedPrimary: {
              border: "2px solid !important",
              "&:hover": { borderWidth: 2 },
            },
            outlinedSecondary: {
              border: "2px solid !important",
              "&:hover": { borderWidth: 2 },
            },
            label: {
              fontWeight: 500,
            },
            outlined: {
              borderWidth: "2px",
              "&:disabled": {
                border: userPrefersDarkMode
                  ? "2px solid rgba(255,255,255,0.35)"
                  : "2px solid rgba(0,0,0,0.35)",
              },
            },
            root: {
              fontSize: "0.875rem !important",
              "&:disabled": {
                color: userPrefersDarkMode
                  ? "rgba(255,255,255,0.5)"
                  : "rgba(0,0,0,0.35)",
              },
            },
          },
          MuiSwitch: {
            switchBase: {
              color: userPrefersDarkMode
                ? "#979797 !important"
                : "#ADADAD !important",
              "&$checked ": {
                color: userPrefersDarkMode
                  ? "#B7FF00 !important"
                  : "#006EFF !important",
              },
            },
            checked: {
              color: userPrefersDarkMode ? "#B7FF00" : "#006EFF",
            },
            track: {
              color: "#878787 ",
            },
            colorPrimary: {
              color: userPrefersDarkMode ? "#B7FF00" : "#006EFF",
            },
            colorSecondary: {
              color: userPrefersDarkMode ? "#D500F9" : "#006EFF",
            },
          },
          MuiFormLabel: {
            root: {
              fontWeight: 500,
            },
          },
          MuiDialogContentText: {
            root: {
              fontWeight: 500,
            },
          },
          MuiListItemText: {
            root: {
              fontWeight: 500,
            },
          },
          MuiTypography: {
            root: {
              fontWeight: "500 !important",
            },
          },
          MuiTab: {
            root: {
              fontSize: "1rem",
            },
          },
          MuiInputLabel: {
            shrink: {
              transform: "translate(0, 8px) scale(0.75);",
            },
          },
          MuiInputBase: {
            input: {
              "&:-webkit-autofill": {
                transitionDelay: "9999s",
                transitionProperty: "background-color, color",
              },
            },
          },
        },
        palette: {
          type: paletteType,
          action: {
            hoverOpacity: 0.1,
          },
          primary: {
            main: userPrefersDarkMode ? "#B7FF00" : "#006EFF",
          },
          secondary: {
            main: userPrefersDarkMode ? "#D500F9" : "#FF6600",
          },
          success: {
            main: userPrefersDarkMode ? "#00E676" : "#00E676",
          },
          info: {
            main: userPrefersDarkMode ? "#29B6F6" : "#29B6F6",
          },
          warning: {
            main: userPrefersDarkMode ? "#FFCA28" : "#FFCA28",
          },
          error: {
            main: userPrefersDarkMode ? "#FF5151" : "#FF0000",
          },
          timeLine: {
            main: userPrefersDarkMode ? "#444950" : "#32373D",
            left: userPrefersDarkMode ? "#2D2D2D" : "#E6E9ED",
          },
          appBarBackground: {
            main: userPrefersDarkMode ? "#1D1D1D" : "#ffffff",
          },
          appBarForeground: {
            main: userPrefersDarkMode ? "#FFFFFF" : "#333333",
          },
          drawerBackground: {
            main: userPrefersDarkMode ? "#1D1D1D" : "#ffffff",
          },
          drawerForeground: {
            main: userPrefersDarkMode ? "#EEEEEE" : "#121212",
          },
          background: {
            paper: userPrefersDarkMode ? "#1d1d1d" : "#ffffff",
            default: userPrefersDarkMode ? "#121212" : "#EEEEEE",
          },
          urlTexts: {
            primary: "#B3B7BD",
          },
          textDark: { main: userPrefersDarkMode ? "#FFFFFF" : "#212121" },
          textLight: { main: userPrefersDarkMode ? "#FFFFFF" : "#212121" },
        },
        tabUpdate: {
          buttonDefault: {
            minWidth: "9em",
          },
        },

        spacing: 8,
      }),
    /*createMuiTheme({
        palette: {
          type: paletteType,
          primary: {
            main: userPrefersDarkMode ? "#F894F6" : cyan["700"]
          },
          secondary: {
            main: userPrefersDarkMode ? cyan["A100"] : cyan["400"]
          },
          success: {
            main: userPrefersDarkMode ? "#50cb06" : " #3CAF3C"
          },
          warning: {
            main: userPrefersDarkMode ? "#F89406" : "#FF6600"
          },
          error: {
            main: userPrefersDarkMode ? "#D62F27" : red[400]
          },
           background: {
            paper: "#1B3D5E",
            default: "#2f353d"
           }
        },
        spacing: 5
      }),*/
    [userPrefersDarkMode, paletteType]
  );

  const language = useSelector((state) => state.language);
  i18n.changeLanguage(language.Current);
  const muiTheme = createMuiTheme(theme);
  return (
    <div className="App">
      <Helmet htmlAttributes={{ lang: language.Current === LANG_TURKISH ? "tr" : "en" }}>
        <title>{language.Current === LANG_TURKISH ? "JetPress | Yönetim Paneli" : "JetPress | Control Panel"}</title>
      </Helmet>
      <ThemeProvider theme={muiTheme}>
        <Router />
      </ThemeProvider>
    </div>
  );
}

export default App;
